import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { chatService } from '../services/chat-service';
import { authService } from '../services/auth-service';
import { AppHeader } from '../components/AppHeader';
import { Breadcrumb } from '../components/Breadcrumb';
import { ConversationMessage } from '../components/ConversationMessage';
import '../styles/markdown.css';
import '../styles/chat-messages.css';
import '../styles/common.css';
import type { ConversationMessageResponse, ConversationResponse } from '../types/chat';
import type { User } from '../types/auth';

function getCurrentQuestion(messages: ConversationMessageResponse[]): ConversationMessageResponse | null {
  if (!messages || messages.length === 0) return null;
  return messages.find(m => !m.answer || m.answer.trim().length === 0) ?? null;
}

export function Conversation() {
  const { subjectId, conversationId: urlConversationId } = useParams<{ subjectId?: string; conversationId?: string }>();
  const navigate = useNavigate();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [currentConversation, setCurrentConversation] = useState<ConversationResponse | null>(null);
  const [messages, setMessages] = useState<ConversationMessageResponse[]>([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [apiResponse, setApiResponse] = useState<string>('');
  const [user, setUser] = useState<User | null>(null);
  const [isInitializing, setIsInitializing] = useState<boolean>(false);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const initializationPromiseRef = useRef<Promise<ConversationResponse> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const currentQuestion = getCurrentQuestion(messages);
  const canAnswer = !!conversationId && !currentConversation?.isCompleted && currentQuestion !== null;
  const visibleMessages = messages.filter(m =>
    (m.answer && m.answer.trim().length > 0) || m === currentQuestion
  );

  const getBackToSubjectsPath = () => {
    const moduleId = sessionStorage.getItem('currentModuleId');
    const classId = sessionStorage.getItem('currentClassId');
    if (!moduleId) return '/subjects';
    return classId ? `/subjects?moduleId=${moduleId}&classId=${classId}` : `/subjects?moduleId=${moduleId}`;
  };

  const subjectNumber = sessionStorage.getItem('currentSubjectNumber') ?? '';
  const subjectTitle = sessionStorage.getItem('currentSubjectTitle') ?? '';
  const conversationTitle = [subjectNumber, subjectTitle].filter(Boolean).join(' - ');


  // Auto-scroll to bottom of messages only when conversation is open (textarea visible)
  useEffect(() => {
    if (!currentConversation?.isCompleted && canAnswer) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, apiResponse, currentConversation?.isCompleted, canAnswer]);

  // Focus text input whenever a new question is shown
  useEffect(() => {
    if (canAnswer && !isLoading) {
      // Small delay to ensure the input is rendered and enabled
      const timer = setTimeout(() => {
        textInputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [canAnswer, currentQuestion?.number, isLoading]);

  // Get current user
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        if (!authService.isAuthenticated()) {
          navigate('/');
          return;
        }
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      } catch (err) {
        navigate('/');
      }
    };

    getCurrentUser();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const answerText = userInput.trim();
    if (!answerText || !conversationId) return;

    // Prüfen, ob mit dieser Antwort die Konversation abgeschlossen wird
    const willComplete = messages.filter(m => !m.answer).length === 1;
    setIsCompleting(willComplete);

    // Letzte Antwort sofort als Chat-Bubble anzeigen
    const questionNumber = currentQuestion?.number;
    if (questionNumber !== undefined) {
      setMessages(prev =>
        prev.map(m => (m.number === questionNumber ? { ...m, answer: answerText } : m))
      );
    }
    setUserInput('');

    try {
      setIsLoading(true);
      setError(null);

      // Submit answer and get updated conversation
      const response = await chatService.submitAnswer(conversationId, answerText);

      // Update the conversation with the latest messages/state
      setMessages(response.messages);
      setCurrentConversation(response);
    } catch (err) {
      setError('Answer could not be sent. Please try again later.');
      // Optimistische Antwort bei Fehler wieder entfernen
      if (questionNumber !== undefined) {
        setMessages(prev =>
          prev.map(m => (m.number === questionNumber ? { ...m, answer: undefined } : m))
        );
      }
    } finally {
      setIsLoading(false);
      setIsCompleting(false);
    }
  };

  const handleCancel = async () => {
    if (!conversationId) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      await chatService.cancelConversation(conversationId);

      // Navigate back to subjects
      navigate(getBackToSubjectsPath());
    } catch (err) {
      setError('Conversation could not be cancelled. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initializeConversation = async () => {
      if (!subjectId && !urlConversationId || isInitializing) {
        return;
      }
  
      try {
        setIsInitializing(true);
        setIsLoading(true);
        
        // If there's already an ongoing initialization, wait for it
        if (initializationPromiseRef.current) {
          const conversation = await initializationPromiseRef.current;
          setConversationId(conversation.id);
          setCurrentConversation(conversation);
          setError(null);
          
          if (conversation.messages && conversation.messages.length > 0) {
            setMessages(conversation.messages);
            const firstMessage = conversation.messages[0];
            if (firstMessage.question) {
              setApiResponse(firstMessage.question);
            }
          }
          return;
        }
        
        let conversation: ConversationResponse;
        
        if (subjectId) {
          // Subject-Click: Initialize für neue Konversation
          const classId = sessionStorage.getItem('currentClassId') ?? undefined;
          initializationPromiseRef.current = chatService.initializeConversation(subjectId, classId);
          conversation = await initializationPromiseRef.current;
        } else if (urlConversationId) {
          // Conversation-Click: Spezifische Konversation laden
          // Reset Promise Ref für Conversation-Clicks
          initializationPromiseRef.current = null;
          
          const rawConversation = await chatService.getConversation(urlConversationId);
          
          // Konvertiere zu ConversationResponse
          conversation = {
            id: rawConversation.id,
            messages: rawConversation.messages.map(m => ({
              question: m.question || '',
              answer: m.answer,
              scorePercentage: m.scorePercentage,
              kiScorePercentage: m.kiScorePercentage,
              improvement: m.improvement,
              number: m.number || 0
            })),
            isCompleted: rawConversation.grade != null && !rawConversation.isAborted,
            isAborted: rawConversation.isAborted,
            grade: rawConversation.grade
          };
          
          setConversationId(rawConversation.id);
          setCurrentConversation(conversation);
        } else {
          throw new Error('No valid ID found');
        }
        
        setConversationId(conversation.id);
        setCurrentConversation(conversation);
        setError(null);
        
        // Check if conversation is already completed
        if (conversation.isCompleted) {
          // Bei abgeschlossenen Konversationen bleiben wir auf der Seite und zeigen das Ergebnis
        }
        
        // Set the messages from the conversation
        if (conversation.messages && conversation.messages.length > 0) {
          setMessages(conversation.messages);
          const firstMessage = conversation.messages[0];
          if (firstMessage.question) {
            setApiResponse(firstMessage.question);
          }
        }
      } catch (err: any) {
        const message = err.response?.data ?? err.message;
        setError(typeof message === 'string' && message.length > 0
          ? message
          : 'Conversation could not be started. Please try again later.');
      } finally {
        setIsLoading(false);
        setIsInitializing(false);
        initializationPromiseRef.current = null;
      }
    };

    initializeConversation();
  }, [subjectId, urlConversationId]);

  if (isLoading && messages.length === 0) {
    return (
      <div className="conversation-container">
        <AppHeader user={user} title="KI Expert" subtitle="Conversation" />
        <main className="list-main">
          <div className="navigation-bar">
            <div className="navigation-bar-header">
              <Breadcrumb items={[
                { label: 'Modules', path: '/modules' },
                { label: 'Subjects', path: getBackToSubjectsPath() },
                { label: 'Conversation' },
              ]} />
              {conversationTitle && <h2 className="navigation-bar-title">{conversationTitle}</h2>}
            </div>
          </div>
          <div className="conversation-surface flex flex-col items-center justify-center p-8">
            <p className="mb-4 text-center">
              Preparing conversation...
              <br />
              The AI is being contacted in the background. This can take a few minutes
            </p>
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  const isAbortedError = error && (error.includes('abgebrochen') || error.includes('Abgebrochen') || error.toLowerCase().includes('aborted'));

  if (error) {
    return (
      <div className="conversation-container">
        <AppHeader user={user} title="KI Expert" subtitle="Conversation" />
        <main className="list-main">
          <div className="navigation-bar">
            <div className="navigation-bar-header">
              <Breadcrumb items={[
                { label: 'Modules', path: '/modules' },
                { label: 'Subjects', path: getBackToSubjectsPath() },
                { label: 'Conversation' },
              ]} />
              {conversationTitle && <h2 className="navigation-bar-title">{conversationTitle}</h2>}
            </div>
          </div>
          <div className="p-4 conversation-error rounded-lg">
            <p>{error}</p>
            {isAbortedError ? (
              <button
                onClick={() => navigate(getBackToSubjectsPath())}
                className="mt-2 btn btn-primary"
              >
                Back to overview
              </button>
            ) : (
              <button
                onClick={() => window.location.reload()}
                className="mt-2 btn btn-primary"
              >
                Try again
              </button>
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="conversation-container">
      <AppHeader 
        user={user} 
        title="KI Expert" 
        subtitle="Conversation" 
      />
      
      <main className="list-main">

        <div className="navigation-bar">
          <div className="navigation-bar-header">
            <Breadcrumb items={[
              { label: 'Modules', path: '/modules' },
              { label: 'Subjects', path: getBackToSubjectsPath() },
              { label: 'Conversation' },
            ]} />
            {conversationTitle && <h2 className="navigation-bar-title">{conversationTitle}</h2>}
          </div>
        </div>
      
      
      <div className="flex flex-col h-full conversation-surface overflow-hidden">

        
        {/* Messages container */}
        <div className="chat-messages-container" style={{ flex: 1, overflowY: 'auto' }}>
        {visibleMessages.map((message, index) => (
          /* Question - Left aligned with gray background */
          /* Answer - Right aligned */
          <ConversationMessage
            key={index}
            question={message.question}
            questionLabel={`${message.number}/${messages.length}`}
            answer={message.answer}
            feedback={message.improvement}
            scorePercentage={message.scorePercentage}
            showEvaluation={currentConversation?.isCompleted && message.scorePercentage !== undefined}
            scoreBadge={message.scorePercentage !== undefined && (
              <div className="chat-score-badge-top-right">
                {Math.round(message.scorePercentage)}%
                {message.kiScorePercentage !== undefined &&
                  Math.round(message.kiScorePercentage) !== Math.round(message.scorePercentage) && (
                  <span className="ki-score-hint">KI: {Math.round(message.kiScorePercentage)}%</span>
                )}
              </div>
            )}
          />
        ))}

        

        {/* Ladeanzeige während der finalen Bewertung */}
        {isCompleting && isLoading && (
          <div className="flex flex-col items-center justify-center p-8 my-4">
            <p className="mb-4 text-center">
              Completing conversation...
              <br />
              The AI is being contacted in the background. This can take a few minutes
            </p>
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area - nur für nicht abgeschlossene Konversationen */}
      {canAnswer && (
        <form onSubmit={handleSubmit} className="p-4 conversation-input-area">
          <div className="flex flex-col space-y-2">
            <textarea
              ref={textInputRef}
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              placeholder="Enter your answer here..."
              className="w-full p-3 conversation-input rounded-lg"
              rows={6}
              disabled={isLoading}
            />
            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={isLoading || !userInput.trim()}
                className="flex-1 px-4 py-2 conversation-submit rounded-lg"
              >
                {isLoading ? 'Sending...' : 'Send'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className="px-4 py-2 conversation-cancel rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Hinweis für abgeschlossene Konversationen */}
      {currentConversation?.isCompleted && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-center">
            <p className="conversation-muted-text mb-2">
              This conversation has been completed.
            </p>
            {currentConversation.grade !== undefined && (
              <div
                className={`inline-block px-4 py-2 rounded-lg ${
                  currentConversation.grade * 100 >= 60 ? 'chat-bubble-content correct' : 'chat-bubble-content incorrect'
                }`}
              >
                <span className="font-semibold">Result:</span> {Math.round(currentConversation.grade * 100)}%
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </main>
    </div>
  );
}
